import { ErrorMessage } from "@/components/ErrorMessage";
import { Button } from "@/components/flexkit/Button";
import { useToast } from "@/components/podkit/toasts/use-toast";
import { WorkflowEditForm } from "@/components/automations/AutomationEditForm";
import { useGetWorkflow, useUpdateWorkflow } from "@/queries/automation-queries";
import { workflowDetailsUrl, workflowName, workflowsUrl } from "@/utils/automations";
import { formatError } from "@/utils/errors";
import { useCallback, useState, type FC } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
    convertFormStepsToWorkflowStep,
    convertFormTriggersToProtobuf,
    type WorkflowEditFormData,
} from "@/components/automations/automation-edit-form-data";
import { WorkflowAction, WorkflowAction_Limits } from "gitpod-next-api/gitpod/v1/workflow_pb";
import Breadcrumbs from "@/components/Breadcrumb";
import { BreadcrumbsSkeleton } from "@/components/BreadcrumbsSkeleton";

export const AutomationEditPage: FC = () => {
    const { workflowId } = useParams<{ workflowId: string }>();
    const navigate = useNavigate();
    const { toast } = useToast();
    const { data: workflow, error: workflowError } = useGetWorkflow(workflowId);
    const updateWorkflow = useUpdateWorkflow();

    const [isFormDisabled, setIsFormDisabled] = useState(true);

    const handleCancel = useCallback(() => {
        if (!workflowId) return;
        navigate(workflowDetailsUrl({ id: workflowId }));
    }, [navigate, workflowId]);

    const onSubmit = useCallback(
        async ({ name, description, executor, steps, triggers, limits }: WorkflowEditFormData) => {
            if (!workflowId) return;
            try {
                await updateWorkflow.mutateAsync({
                    workflowId,
                    name,
                    description,
                    executor: executor,
                    action: new WorkflowAction({
                        limits: new WorkflowAction_Limits({
                            maxParallel: limits?.maxParallel ?? 10,
                            maxTotal: limits?.maxTotal ?? 100,
                        }),
                        steps: convertFormStepsToWorkflowStep(steps),
                    }),
                    triggers: triggers ? convertFormTriggersToProtobuf(triggers) : undefined,
                });
                navigate(workflowDetailsUrl({ id: workflowId }));
            } catch (error) {
                toast({
                    title: "Failed to update automation",
                    description: formatError(error),
                });
            }
        },
        [workflowId, updateWorkflow, toast, navigate],
    );

    const isSubmitting = updateWorkflow.isPending;
    return (
        <div data-testid="workflow-edit-page" className="flex flex-col gap-4">
            {workflow ? (
                <div className="flex items-start justify-between">
                    <Breadcrumbs
                        customBreadcrumbs={[
                            { label: "Automations", to: workflowsUrl() },
                            { label: workflowName(workflow) },
                        ]}
                    />
                    <div className="flex items-center gap-2">
                        <Button
                            variant="outline"
                            onClick={handleCancel}
                            disabled={isSubmitting}
                            data-tracking-id="cancel-workflow-edit-page"
                        >
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            form="edit-workflow-form"
                            disabled={isFormDisabled || isSubmitting}
                            loading={isSubmitting}
                            data-tracking-id="save-changes-workflow-edit-page"
                            variant="primary"
                        >
                            Save
                        </Button>
                    </div>
                </div>
            ) : (
                <BreadcrumbsSkeleton itemsCount={2} />
            )}

            <ErrorMessage error={workflowError} />

            {workflow && (
                <div className="flex flex-col gap-6">
                    <WorkflowEditForm
                        workflow={workflow}
                        formName="edit-workflow-form"
                        onDisabledChange={setIsFormDisabled}
                        onSubmit={onSubmit}
                    />
                </div>
            )}
        </div>
    );
};
